import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ChevronRight, Users } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ParentLayout } from "@/components/parent/ParentLayout";

interface Child {
  id: string;
  firstName: string;
  lastName: string;
  grade: number;
  school: string | null;
  profilePhotoUrl: string | null;
  subscriptionStatus: string;
  accuracy: number;
  totalQuestions: number;
  currentLevel: number;
  lastActiveAt: string | null;
}

export default function ParentChildren() {
  const { data, isLoading } = useQuery<{ children: Child[] }>({ queryKey: ["/api/parent/children"] });
  const children = data?.children ?? [];

  return (
    <ParentLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Children</h1>
          <p className="text-sm text-muted-foreground">Every child linked to your account.</p>
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {[0, 1, 2].map((i) => <Skeleton key={i} className="h-20" />)}
          </div>
        ) : children.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center space-y-3">
              <Users className="w-10 h-10 mx-auto text-muted-foreground" />
              <p className="font-semibold">No children linked yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {children.map((c) => {
              const initials = `${c.firstName.charAt(0)}${c.lastName.charAt(0)}`.toUpperCase();
              return (
                <Card key={c.id}>
                  <CardContent className="p-4 flex items-center gap-4">
                    <Avatar className="h-12 w-12 shrink-0">
                      {c.profilePhotoUrl && <AvatarImage src={c.profilePhotoUrl} alt={`${c.firstName} ${c.lastName}`} />}
                      <AvatarFallback>{initials}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold truncate">{c.firstName} {c.lastName}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        Grade {c.grade}
                        {c.school ? ` · ${c.school}` : ""}
                        {c.lastActiveAt ? ` · ${formatDistanceToNow(new Date(c.lastActiveAt), { addSuffix: true })}` : ""}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Level {c.currentLevel} · {c.accuracy}% accuracy · {c.totalQuestions} questions
                      </p>
                    </div>
                    <Badge variant={c.subscriptionStatus === "active" ? "default" : "outline"}>{c.subscriptionStatus}</Badge>
                    <Button asChild variant="ghost" size="icon" data-testid={`child-row-link-${c.id}`}>
                      <Link href={`/parent/children/${c.id}`}>
                        <ChevronRight className="h-5 w-5" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </ParentLayout>
  );
}
